import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  ShoppingCart as PlaceOrderIcon,
  CallReceived as ReceiveIcon,
} from '@mui/icons-material';
import { IconPlus, IconExternalLink } from '@tabler/icons-react';
import {
  SecondaryToolbar,
  ToolbarRadio,
  ToolbarSpacer,
  ToolbarDivider,
  ToolbarCount,
  ToolbarButton,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../components/table';
import { Chip } from '../components/chip';
import type { ChipTone } from '../components/chip';
import type { FreestandingLineKind, PartOrderLine, PartOrderLineStatus } from '@techgitter/contracts';
import { ALL_PART_ORDER_LINE_STATUSES } from '@techgitter/contracts';
import { cancelLine, fetchPartOrderLines, reprintReceiptLabel } from '../services/partOrdersApi';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useSignalREvent } from '../hooks/useSignalR';
import { AddStockLinePanel } from '../components/part_orders/AddStockLinePanel';
import { EditPartOrderDialog } from '../components/part_orders/EditPartOrderDialog';
import { PlaceOrderDialog } from '../components/part_orders/PlaceOrderDialog';
import { ReceiveLinePanel } from '../components/part_orders/ReceiveLinePanel';

type StatusFilter = PartOrderLineStatus | 'all';

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: 'All',
  to_order: 'To Order',
  ordered: 'Ordered',
  partially_received: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled',
};

const STATUS_ORDER: StatusFilter[] = [
  'all',
  'to_order',
  'ordered',
  'partially_received',
  'received',
  'cancelled',
];

const STATUS_CHIP_COLOR: Record<PartOrderLineStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  to_order: 'warning',
  ordered: 'info',
  partially_received: 'info',
  received: 'success',
  cancelled: 'default',
};

const statusTone = (s: PartOrderLineStatus): ChipTone => {
  const c = STATUS_CHIP_COLOR[s];
  return c === 'default' ? 'neutral' : c;
};

type SortField = 'vendor' | 'orderDate';
type SortDir = 'asc' | 'desc';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

/** Flex row used in place of MUI `<Stack direction="row">` — same layout, no
 * emotion/styled cost. Kept tiny because it renders once per table row. */
const rowFlex = (justify: 'flex-start' | 'flex-end'): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: justify,
  gap: 4,
});

/**
 * A received line carries a reprintable customer label only when it actually
 * had one: customer-job / quote lines with a customer name. Stock / internal-use
 * lines have no customer, so the receipt printed nothing and there's nothing to
 * reprint — mirrors the server's `part-receipt-label` applicability check.
 */
const canReprintLabel = (line: PartOrderLine): boolean =>
  line.status === 'received' &&
  line.triggeredBy !== 'stock' &&
  line.triggeredBy !== 'internal_use' &&
  !!line.customerName;

interface PartOrderRowProps {
  line: PartOrderLine;
  status: StatusFilter;
  canMutate: boolean;
  selected: boolean;
  reprinting: boolean;
  onToggleSelect: (lineId: string) => void;
  onEdit: (partOrderId: string) => void;
  onEditLine: (line: PartOrderLine) => void;
  onReceive: (line: PartOrderLine) => void;
  onReprint: (line: PartOrderLine) => void;
  onCancel: (line: PartOrderLine) => void;
}

/**
 * A single part-order line row. Memoised so unrelated page state changes
 * (sort toggle, snackbar, SignalR refresh that returns identical rows) don't
 * re-render every row. Deliberately avoids MUI `Tooltip`/`IconButton`/`Stack`
 * — at 39+ rows their emotion/Popper/ripple cost dominates the render; native
 * `title` + plain buttons render the same but ~10× cheaper.
 */
const PartOrderRow: React.FC<PartOrderRowProps> = React.memo(
  ({ line, status, canMutate, selected, reprinting, onToggleSelect, onEdit, onEditLine, onReceive, onReprint, onCancel }) => {
    const isToOrder = line.status === 'to_order';
    const isOrdered = line.status === 'ordered' || line.status === 'partially_received';
    return (
      <DataTableRow>
        {status === 'to_order' && canMutate && (
          <DataTableCell style={{ width: 36 }}>
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(line.id)}
            />
          </DataTableCell>
        )}
        <DataTableCell>{formatDate(line.orderDate)}</DataTableCell>
        <DataTableCell>
          <div
            title={line.vendorName || undefined}
            style={{
              maxWidth: 320,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {line.vendorName || '—'}
          </div>
        </DataTableCell>
        <DataTableCell>{line.customerName || '—'}</DataTableCell>
        <DataTableCell>
          {line.triggeredBy === 'stock'
            ? <em>Stock</em>
            : line.triggeredBy === 'internal_use'
              ? <em>Internal Use</em>
              : line.quoteNumber || (line.quoteId ? line.quoteId.slice(0, 8) : '—')}
        </DataTableCell>
        <DataTableCell>{line.vendorOrderNumber || '—'}</DataTableCell>
        <DataTableCell>
          <div style={rowFlex('flex-start')}>
            <div
              title={line.description}
              style={{
                maxWidth: line.productUrl ? 296 : 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {line.description}
            </div>
            {line.productUrl && (
              <a
                href={line.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open product link"
                style={{ display: 'inline-flex', color: '#4a90d9' }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconExternalLink size={15} stroke={1.5} />
              </a>
            )}
          </div>
        </DataTableCell>
        <DataTableCell align="right">{line.qty}</DataTableCell>
        <DataTableCell>{line.workitemId ? line.workitemId.slice(0, 8) : '—'}</DataTableCell>
        <DataTableCell>
          <div style={rowFlex('flex-start')}>
            {line.fulfillmentMethod === 'pickup' && (
              <Chip tone="info" title="Collected from vendor — ready-for-collection date">
                Pickup
              </Chip>
            )}
            <span>{formatDate(line.expectedDeliveryDate)}</span>
          </div>
        </DataTableCell>
        <DataTableCell>
          <span title="Expected part receipt date — order date + this part's lead time">
            {formatDate(line.expectedReceiptDate)}
          </span>
        </DataTableCell>
        {status === 'received' && (
          <DataTableCell>{formatDate(line.receivedDate)}</DataTableCell>
        )}
        <DataTableCell>
          <span title={line.notes || undefined}>
            {line.notes ? `${line.notes.slice(0, 30)}${line.notes.length > 30 ? '…' : ''}` : '—'}
          </span>
        </DataTableCell>
        {status === 'all' && (
          <DataTableCell>
            <Chip tone={statusTone(line.status)}>
              {STATUS_LABELS[line.status] ?? line.status}
            </Chip>
          </DataTableCell>
        )}
        {canMutate && (
          <DataTableCell align="right">
            <div style={rowFlex('flex-end')}>
              {isToOrder && (
                <IconButton
                  size="small"
                  disableRipple
                  title="Edit line — supplier, price, URL"
                  aria-label="Edit line"
                  onClick={() => onEditLine(line)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {line.partOrderId && (
                <IconButton
                  size="small"
                  disableRipple
                  title="Edit PO header"
                  onClick={() => onEdit(line.partOrderId!)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {isOrdered && (
                <IconButton
                  size="small"
                  disableRipple
                  title="Record receipt"
                  onClick={() => onReceive(line)}
                >
                  <ReceiveIcon fontSize="small" />
                </IconButton>
              )}
              {(isToOrder || isOrdered) && (
                <IconButton
                  size="small"
                  disableRipple
                  title={isToOrder ? 'Remove from basket' : 'Cancel line'}
                  onClick={() => onCancel(line)}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              )}
              {canReprintLabel(line) && (
                <IconButton
                  size="small"
                  disableRipple
                  title="Reprint receipt label"
                  onClick={() => onReprint(line)}
                  disabled={reprinting}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              )}
              {line.status === 'received' && <CheckIcon fontSize="small" color="success" />}
            </div>
          </DataTableCell>
        )}
      </DataTableRow>
    );
  },
);
PartOrderRow.displayName = 'PartOrderRow';

/**
 * Part Orders — single workshop surface for the basket → ordered → received
 * lifecycle. Visible to every authenticated user; mutation controls are gated
 * on Purchasing or Admin.
 */
export const PartOrdersPage: React.FC = () => {
  const currentUser = useCurrentUser();
  const canMutate = !!currentUser?.hasAnyRole('Purchasing', 'Admin');

  const initialStatus = useMemo<StatusFilter>(() => {
    const qs = new URLSearchParams(window.location.search);
    const requested = qs.get('status') as StatusFilter | null;
    if (requested && ALL_PART_ORDER_LINE_STATUSES.includes(requested as PartOrderLineStatus)) {
      return requested;
    }
    if (requested === 'all') return 'all';
    return 'to_order';
  }, []);

  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [lines, setLines] = useState<PartOrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('vendor');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [placeOrderFor, setPlaceOrderFor] = useState<{ vendorId: string; vendorName: string; lines: PartOrderLine[] } | null>(null);
  const [receiveFor, setReceiveFor] = useState<PartOrderLine | null>(null);
  const [addLineKind, setAddLineKind] = useState<FreestandingLineKind | null>(null);
  const [editLineFor, setEditLineFor] = useState<PartOrderLine | null>(null);
  const [editPartOrderId, setEditPartOrderId] = useState<string | null>(null);
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setLoading(true);
        setLoadError(null);
        const items = await fetchPartOrderLines({ status }, signal);
        setLines(items);
        setSelected({});
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load part order lines');
      } finally {
        setLoading(false);
      }
    },
    [status],
  );

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  // Live updates from SignalR. Any part-order event triggers a list refresh.
  useSignalREvent('parts:to_order', () => refresh());
  useSignalREvent('parts:order_placed', () => refresh());
  useSignalREvent('parts:line_received', () => refresh());

  const sortedLines = useMemo(() => {
    const arr = [...lines];
    arr.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'vendor') {
        const cmp = (a.vendorName || '').localeCompare(b.vendorName || '');
        if (cmp !== 0) return cmp * dir;
        return (a.orderDate || '').localeCompare(b.orderDate || '') * dir;
      }
      // orderDate
      const cmp = (a.orderDate || '').localeCompare(b.orderDate || '');
      if (cmp !== 0) return cmp * dir;
      return (a.vendorName || '').localeCompare(b.vendorName || '') * dir;
    });
    return arr;
  }, [lines, sortField, sortDir]);

  const groupedByVendor = useMemo(() => {
    if (status !== 'to_order') return null;
    const groups = new Map<string, { vendorId: string; vendorName: string; lines: PartOrderLine[] }>();
    for (const line of sortedLines) {
      const key = line.vendorId;
      const existing = groups.get(key);
      if (existing) {
        existing.lines.push(line);
      } else {
        groups.set(key, { vendorId: line.vendorId, vendorName: line.vendorName || '(no vendor)', lines: [line] });
      }
    }
    return Array.from(groups.values());
  }, [sortedLines, status]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleSelect = useCallback((lineId: string) => {
    setSelected((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
  }, []);

  const handleEdit = useCallback((partOrderId: string) => setEditPartOrderId(partOrderId), []);
  // Receive, Add-line and Edit-line share the single right-column slot —
  // opening one closes the others so they never collide.
  const handleReceive = useCallback((line: PartOrderLine) => {
    setReceiveFor(line);
    setAddLineKind(null);
    setEditLineFor(null);
  }, []);
  const openAddLine = useCallback((kind: FreestandingLineKind) => {
    setAddLineKind(kind);
    setReceiveFor(null);
    setEditLineFor(null);
  }, []);
  const handleEditLine = useCallback((line: PartOrderLine) => {
    setEditLineFor(line);
    setAddLineKind(null);
    setReceiveFor(null);
  }, []);

  // Reprint the receipt customer label (one copy) for an already-received line.
  // No list refresh — reprinting doesn't change the line; the in-flight id just
  // disables the button so a double-click can't double-print.
  const handleReprint = useCallback(async (line: PartOrderLine) => {
    setReprintingId(line.id);
    try {
      const { published } = await reprintReceiptLabel(line.id, 1);
      setSnackbar(
        published
          ? { open: true, message: 'Label sent to printer.', severity: 'success' }
          : { open: true, message: 'No label printed — check the receipt-label print rule.', severity: 'info' },
      );
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Reprint failed', severity: 'error' });
    } finally {
      setReprintingId(null);
    }
  }, []);

  const openPlaceOrderForVendor = (group: { vendorId: string; vendorName: string; lines: PartOrderLine[] }) => {
    const chosen = group.lines.filter((l) => selected[l.id]);
    const target = chosen.length > 0 ? chosen : group.lines;
    setPlaceOrderFor({ vendorId: group.vendorId, vendorName: group.vendorName, lines: target });
  };

  const handlePlaceOrderClose = (placed: boolean) => {
    setPlaceOrderFor(null);
    if (placed) {
      setSnackbar({ open: true, message: 'Order placed.', severity: 'success' });
      refresh();
    }
  };

  const handleReceiveClose = (received: boolean) => {
    setReceiveFor(null);
    if (received) {
      setSnackbar({ open: true, message: 'Receipt recorded.', severity: 'success' });
      refresh();
    }
  };

  const handleCancel = useCallback(async (line: PartOrderLine) => {
    const verb = line.status === 'to_order' ? 'remove' : 'cancel';
    if (!window.confirm(`${verb === 'remove' ? 'Remove' : 'Cancel'} ${line.description}?`)) return;
    try {
      await cancelLine(line.id);
      setSnackbar({ open: true, message: `Line ${verb}led.`, severity: 'success' });
      refresh();
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Cancel failed', severity: 'error' });
    }
  }, [refresh]);

  const renderRow = (line: PartOrderLine) => (
    <PartOrderRow
      key={line.id}
      line={line}
      status={status}
      canMutate={canMutate}
      selected={!!selected[line.id]}
      reprinting={reprintingId === line.id}
      onToggleSelect={toggleSelect}
      onEdit={handleEdit}
      onEditLine={handleEditLine}
      onReceive={handleReceive}
      onReprint={handleReprint}
      onCancel={handleCancel}
    />
  );

  const columnCount =
    // order date, vendor, customer, quote, supplier order, desc, qty, job,
    // due date, expected receipt, notes
    11 +
    (status === 'received' ? 1 : 0) + // receipt date (received view only)
    (status === 'all' ? 1 : 0) + // status (all view only)
    (status === 'to_order' && canMutate ? 1 : 0) + // checkbox col
    (canMutate ? 1 : 0); // actions col

  // The right column hosts either the Add-line panel or the Receive panel.
  const panelOpen = addLineKind !== null || receiveFor !== null || editLineFor !== null;

  return (
    <>
      {/* Contextual toolbar — floating card on the grey page surface
          (main-content--part-orders), matching the Invoices page. */}
      <SecondaryToolbar floating>
        {STATUS_ORDER.map((s) => (
          <ToolbarRadio
            key={s}
            name="part-order-status"
            label={STATUS_LABELS[s]}
            active={s === status}
            onClick={() => setStatus(s)}
          />
        ))}
        <ToolbarSpacer />
        <ToolbarCount>
          {!canMutate
            ? 'Read-only — Purchasing or Admin role required to place / receive orders.'
            : `${sortedLines.length} line${sortedLines.length === 1 ? '' : 's'}`}
        </ToolbarCount>
        {canMutate && (
          <>
            <ToolbarDivider />
            <ToolbarButton variant="primary" icon={IconPlus} onClick={() => openAddLine('stock')}>
              New Part Order
            </ToolbarButton>
          </>
        )}
      </SecondaryToolbar>

      {/* Two-column layout — content left, Add stock line panel right. Collapses
          to a single column when the panel is closed. */}
      <div className={`part-orders-layout${panelOpen ? '' : ' part-orders-layout--panel-closed'}`}>
      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto', minWidth: 0, width: '100%' }}>
        {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : sortedLines.length === 0 ? (
        <DataTableCard>
          <Box sx={{ p: 4, textAlign: 'center', color: '#888', fontSize: 14 }}>
            No lines in this view.
          </Box>
        </DataTableCard>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                {status === 'to_order' && canMutate && <DataTableHeadCell width={36} />}
                <DataTableHeadCell sortable active={sortField === 'orderDate'} direction={sortDir} onSort={() => toggleSort('orderDate')}>
                  Order Date
                </DataTableHeadCell>
                <DataTableHeadCell sortable active={sortField === 'vendor'} direction={sortDir} onSort={() => toggleSort('vendor')}>
                  Vendor
                </DataTableHeadCell>
                <DataTableHeadCell>Customer</DataTableHeadCell>
                <DataTableHeadCell>Quote</DataTableHeadCell>
                <DataTableHeadCell>Supplier Order</DataTableHeadCell>
                <DataTableHeadCell>Description</DataTableHeadCell>
                <DataTableHeadCell align="right">Qty</DataTableHeadCell>
                <DataTableHeadCell>Job</DataTableHeadCell>
                <DataTableHeadCell>Due Date</DataTableHeadCell>
                <DataTableHeadCell>Expected Receipt</DataTableHeadCell>
                {status === 'received' && <DataTableHeadCell>Receipt Date</DataTableHeadCell>}
                <DataTableHeadCell>Notes</DataTableHeadCell>
                {status === 'all' && <DataTableHeadCell>Status</DataTableHeadCell>}
                {canMutate && <DataTableHeadCell align="right">Actions</DataTableHeadCell>}
              </tr>
            </thead>
            <tbody>
              {groupedByVendor
                ? groupedByVendor.map((group) => (
                    <React.Fragment key={group.vendorId}>
                      <tr>
                        <td
                          colSpan={columnCount}
                          style={{
                            background: '#fafbfd',
                            borderBottom: '0.5px solid #e2eaf3',
                            padding: '6px 10px',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">
                              {group.vendorName} — {group.lines.length} line{group.lines.length === 1 ? '' : 's'}
                            </Typography>
                            {canMutate && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlaceOrderIcon />}
                                onClick={() => openPlaceOrderForVendor(group)}
                              >
                                Place order for {group.vendorName}
                              </Button>
                            )}
                          </Stack>
                        </td>
                      </tr>
                      {group.lines.map(renderRow)}
                    </React.Fragment>
                  ))
                : sortedLines.map(renderRow)}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}

      {placeOrderFor && (
        <PlaceOrderDialog
          open
          vendorId={placeOrderFor.vendorId}
          vendorName={placeOrderFor.vendorName}
          lines={placeOrderFor.lines}
          onClose={handlePlaceOrderClose}
        />
      )}

      <EditPartOrderDialog
        open={editPartOrderId !== null}
        partOrderId={editPartOrderId}
        onClose={(updated) => {
          setEditPartOrderId(null);
          if (updated) {
            setSnackbar({ open: true, message: 'Part order updated.', severity: 'success' });
            refresh();
          }
        }}
      />



      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>

      {panelOpen && (
        <div className="part-orders-panel-col">
          {receiveFor ? (
            <ReceiveLinePanel line={receiveFor} onClose={handleReceiveClose} />
          ) : editLineFor ? (
            <AddStockLinePanel
              key={editLineFor.id}
              line={editLineFor}
              onClose={(saved) => {
                setEditLineFor(null);
                if (saved) {
                  setSnackbar({ open: true, message: 'Line updated.', severity: 'success' });
                  refresh();
                }
              }}
            />
          ) : (
            <AddStockLinePanel
              initialKind={addLineKind ?? 'stock'}
              onClose={(created) => {
                const kind = addLineKind;
                setAddLineKind(null);
                if (created) {
                  const label = kind === 'internal_use' ? 'Internal-use' : 'Stock';
                  setSnackbar({ open: true, message: `${label} line added to basket.`, severity: 'success' });
                  refresh();
                }
              }}
            />
          )}
        </div>
      )}
      </div>
    </>
  );
};
